import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { readAlerts } from '../alert.api';
import { QueryKey } from '@/api/common/types/query-key.enum';
import logger from '@/services/logger';
import type { Alert } from '../types/alert';

export const useReadAlerts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: readAlerts,
    onMutate: async (alertIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: [QueryKey.GetAlerts] });

      // Store the previous alerts data
      const previousAlerts = queryClient.getQueryData<Alert[]>([QueryKey.GetAlerts]);

      // Only update if we have existing data
      if (previousAlerts) {
        queryClient.setQueryData<Alert[]>([QueryKey.GetAlerts], oldData => {
          if (!oldData) return previousAlerts;
          return oldData.map(alert => {
            if (alertIds.includes(alert.id)) {
              return { ...alert, readAt: new Date().toISOString() };
            }
            return alert;
          });
        });
      } else {
        queryClient.refetchQueries({ queryKey: [QueryKey.GetAlerts] });
      }

      return { previousAlerts };
    },
    onError: (err, _, context) => {
      logger.error({
        message: '[useReadAlerts] - Failed to read alerts after retries',
        data: {},
        error: err as Error,
      });
      // Restore the previous alerts data on error
      if (context?.previousAlerts) {
        queryClient.setQueryData([QueryKey.GetAlerts], context.previousAlerts);
      }
    },
  });
};
